<template>
  <section
    class="pb"
    :aria-label="sourceMixAria"
    :title="sourceMixTooltip"
  >
    <header class="pb__header">
      <div>
        <span class="pb__eyebrow">Source Mix</span>
        <h3 class="pb__title">Left / Center / Right</h3>
        <p class="pb__hint" tabindex="0">{{ sourceMixTooltip }}</p>
      </div>
      <strong class="pb__total">{{ totalSources }} sources</strong>
    </header>

    <div v-if="hasSources" class="pb__rail" aria-hidden="true">
      <span
        v-for="perspective in perspectives"
        :key="perspective.key"
        class="pb__segment"
        :class="`pb__segment--${perspective.key}`"
        :style="{ width: perspective.percent }"
      ></span>
    </div>

    <div v-else class="pb__empty">
      No perspective spread yet.
    </div>

    <div class="pb__rows">
      <article
        v-for="perspective in perspectives"
        :key="perspective.key"
        class="pb__row"
        :class="`pb__row--${perspective.key}`"
      >
        <div class="pb__identity">
          <span class="pb__dot" aria-hidden="true"></span>
          <div>
            <h4 class="pb__label">{{ perspective.label }}</h4>
            <p class="pb__share">{{ perspective.count }} {{ perspective.count === 1 ? 'source' : 'sources' }}</p>
          </div>
        </div>

        <div class="pb__metrics">
          <strong class="pb__percent">{{ perspective.percentLabel }}</strong>
          <span v-if="showSentiment && perspective.hasSentiment" class="pb__sentiment">
            <span aria-hidden="true">{{ perspective.emoji }}</span>
            Avg {{ perspective.avgSentiment }}/100
          </span>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { getPerspectiveLabel, getSentimentEmoji } from '@/utils/coverageHelpers'
import { SOURCE_MIX_TOOLTIP } from '@/utils/framingHelpers'

const PERSPECTIVE_KEYS = ['left', 'center', 'right']

function toCount(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? Math.round(number) : 0
}

function toSentiment(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  return Math.max(0, Math.min(100, Math.round(number)))
}

export default {
  name: 'PerspectiveBar',

  props: {
    biasBreakdown: {
      type: Object,
      default: null,
    },
    perspectiveStats: {
      type: Object,
      default: null,
    },
    showSentiment: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      sourceMixTooltip: SOURCE_MIX_TOOLTIP,
    }
  },

  computed: {
    counts() {
      const breakdown = this.biasBreakdown || {}
      return {
        left: toCount(breakdown.left),
        center: toCount(breakdown.center),
        right: toCount(breakdown.right),
      }
    },

    totalSources() {
      return PERSPECTIVE_KEYS.reduce((sum, key) => sum + this.counts[key], 0)
    },

    hasSources() {
      return this.totalSources > 0
    },

    sourceMixAria() {
      return `Source mix: ${this.counts.left} left, ${this.counts.center} center, ${this.counts.right} right sources. ${SOURCE_MIX_TOOLTIP}`
    },

    perspectives() {
      const stats = this.perspectiveStats || {}

      return PERSPECTIVE_KEYS.map((key) => {
        const count = this.counts[key]
        const percentNumber = this.hasSources ? Math.round((count / this.totalSources) * 100) : 0
        const avgSentiment = toSentiment(stats[key]?.avgSentiment)

        return {
          key,
          count,
          label: getPerspectiveLabel(key),
          percent: `${percentNumber}%`,
          percentLabel: `${percentNumber}%`,
          avgSentiment,
          hasSentiment: avgSentiment !== null,
          emoji: getSentimentEmoji(avgSentiment),
        }
      })
    },
  },
}
</script>

<style scoped>
.pb {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 24px;
  padding: 18px;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.08), transparent 38%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
}

.pb__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.pb__eyebrow {
  display: block;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  line-height: 1;
  text-transform: uppercase;
}

.pb__title {
  margin: 6px 0 0;
  color: #0f172a;
  font-size: clamp(1.2rem, 4vw, 1.65rem);
  font-weight: 950;
  letter-spacing: -0.045em;
  line-height: 1.05;
}

.pb__hint {
  margin: 6px 0 0;
  font-size: 0.72rem;
  line-height: 1.35;
  color: #64748b;
  font-weight: 500;
  max-width: 28rem;
  outline: none;
}

.pb__hint:focus {
  color: #334155;
  text-decoration: underline;
}

.pb__total {
  flex: 0 0 auto;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 999px;
  padding: 8px 11px;
  background: #ffffff;
  color: #111827;
  font-size: 0.86rem;
  font-weight: 900;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.pb__rail {
  display: flex;
  overflow: hidden;
  height: 15px;
  border-radius: 999px;
  background: #e5e7eb;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.12);
}

.pb__segment {
  min-width: 0;
  transition: width 420ms ease;
}

.pb__segment--left {
  background: linear-gradient(90deg, #60a5fa, #2563eb);
}

.pb__segment--center {
  background: linear-gradient(90deg, #a3a3a3, #525252);
}

.pb__segment--right {
  background: linear-gradient(90deg, #f87171, #dc2626);
}

.pb__empty {
  border: 1px dashed rgba(100, 116, 139, 0.34);
  border-radius: 16px;
  padding: 14px;
  background: rgba(248, 250, 252, 0.82);
  color: #64748b;
  font-size: 0.92rem;
  font-weight: 800;
  text-align: center;
}

.pb__rows {
  display: grid;
  gap: 10px;
  margin-top: 16px;
}

.pb__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 18px;
  padding: 13px 14px;
  background: rgba(255, 255, 255, 0.76);
}

.pb__identity {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
}

.pb__dot {
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  box-shadow: 0 0 0 5px rgba(148, 163, 184, 0.12);
}

.pb__row--left .pb__dot {
  background: #2563eb;
}

.pb__row--center .pb__dot {
  background: #525252;
}

.pb__row--right .pb__dot {
  background: #dc2626;
}

.pb__label {
  margin: 0;
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 900;
  letter-spacing: -0.015em;
}

.pb__share {
  margin: 3px 0 0;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 800;
}

.pb__metrics {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 5px;
  text-align: right;
}

.pb__percent {
  color: #111827;
  font-size: 1.05rem;
  font-weight: 950;
  letter-spacing: -0.04em;
}

.pb__sentiment {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  padding: 5px 8px;
  background: #f1f5f9;
  color: #334155;
  font-size: 0.74rem;
  font-weight: 850;
  white-space: nowrap;
}

@media (max-width: 640px) {
  .pb {
    border-radius: 20px;
    padding: 15px;
  }

  .pb__header {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .pb__total {
    width: fit-content;
  }

  .pb__row {
    align-items: flex-start;
    flex-direction: column;
  }

  .pb__metrics {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    text-align: left;
  }
}
</style>
